import Immutable from "immutable";
import { PropNames } from "@showitinc/merge-utils";
import {
  FORM_STATE_DEFAULT,
  FORM_STATE_ERROR,
  FORM_STATE_FOCUS,
} from "@/constants/FormFieldConstants.mjs";
import SystemFonts from "@/constants/SystemFonts";
import { formatBorderRadius } from "./ButtonStyleResolver";
import { gatherProps } from "./Merge/MergeUtils.mjs";
import StyleUtils from "./StyleUtils";

const {
  CONTROL_ARROW_COLOR,
  CONTROL_ARROW_SIZE,
  CONTROL_ARROW_STYLE,
  CONTROL_BACKGROUND_COLOR,
  CONTROL_BORDER_COLOR,
  CONTROL_BORDER_RADIUS,
  CONTROL_BORDER_STYLE,
  CONTROL_BORDER_WIDTH,
  CONTROL_CHECKBOX_STYLE,
  CONTROL_PADDING,
  CONTROL_RADIO_DOT_COLOR,
  CONTROL_RADIO_DOT_SIZE,
  CONTROL_RADIO_STYLE,
  CONTROL_SHADOW_BLUR,
  CONTROL_SHADOW_COLOR,
  CONTROL_SHADOW_OFFSET_X,
  CONTROL_SHADOW_OFFSET_Y,
  CONTROL_SHADOW_STYLE,
  CONTROL_TEXT_COLOR,
  CONTROL_TEXT_FONT,
  CONTROL_TEXT_PLACEHOLDER_COLOR,
  CONTROL_TEXT_SIZE,
  LABEL_COLOR,
  LABEL_FONT,
  LABEL_GAP,
  LABEL_POSITION,
  LABEL_REQUIRED_COLOR,
  LABEL_REQUIRED_PLACEMENT,
  LABEL_SIZE,
  LABEL_WIDTH,
  RADIO_DIRECTION,
  RADIO_GAP,
} = PropNames;

/** @typedef {import("immutable").Map} ImmutableMap */

const px = (value) => (typeof value === "number" ? `${value}px` : String(value));
const raw = (value) => String(value);

/**
 * @param {string} value
 * @param {ImmutableMap} theme
 * @returns {string}
 */
const color = (value, theme) => StyleUtils.getColorValue(String(value), theme);

/**
 * A theme font name resolves to its family; anything else is taken as a family already.
 * @param {string} value
 * @param {ImmutableMap} theme
 * @returns {string}
 */
const font = (value, theme) =>
  theme.getIn(["fonts", value, "family"]) ?? SystemFonts[value]?.family ?? value;

/**
 * Props a state can override, resolved for the state being drawn. Names match the custom
 * properties in protorenderer's widget docs (`apps/engine/widgets/*.md`).
 */
const STATE_VARS = [
  ["--bgColor", CONTROL_BACKGROUND_COLOR, color],
  ["--borderColor", CONTROL_BORDER_COLOR, color],
  ["--borderWidth", CONTROL_BORDER_WIDTH, px],
  ["--borderStyle", CONTROL_BORDER_STYLE, raw],
  ["--borderRadius", CONTROL_BORDER_RADIUS, formatBorderRadius],
  ["--padding", CONTROL_PADDING, px],
  ["--inputFont", CONTROL_TEXT_FONT, font],
  ["--inputSize", CONTROL_TEXT_SIZE, px],
  ["--inputColor", CONTROL_TEXT_COLOR, color],
  ["--placeholderColor", CONTROL_TEXT_PLACEHOLDER_COLOR, color],
  ["--labelFont", LABEL_FONT, font],
  ["--labelSize", LABEL_SIZE, px],
  ["--labelColor", LABEL_COLOR, color],
  ["--requiredColor", LABEL_REQUIRED_COLOR, color],
  ["--arrowColor", CONTROL_ARROW_COLOR, color],
  ["--arrowSize", CONTROL_ARROW_SIZE, px],
  ["--radioDotColor", CONTROL_RADIO_DOT_COLOR, color],
  ["--radioDotSize", CONTROL_RADIO_DOT_SIZE, px],
];

/** Structural props: one value, read for the Default state only. */
const DEFAULT_ONLY_VARS = [
  ["--labelGap", LABEL_GAP, px],
  ["--labelWidth", LABEL_WIDTH, px],
  ["--radioGap", RADIO_GAP, px],
];

/** Enums the widgets take as reflected attributes, keyed by widget property; values pass as-is. */
const WIDGET_ATTRIBUTES = [
  ["labelPosition", LABEL_POSITION],
  ["requiredPlacement", LABEL_REQUIRED_PLACEMENT],
  ["arrowStyle", CONTROL_ARROW_STYLE],
  ["radioDirection", RADIO_DIRECTION],
  ["radioStyle", CONTROL_RADIO_STYLE],
  ["checkboxStyle", CONTROL_CHECKBOX_STYLE],
];

const SHADOW_VARS = {
  outer: ["--shadowX", "--shadowY", "--shadowBlur", "--shadowColor"],
  inner: ["--innerShadowX", "--innerShadowY", "--innerShadowBlur", "--innerShadowColor"],
};

const SHADOW_PROPS = [
  CONTROL_SHADOW_STYLE,
  CONTROL_SHADOW_COLOR,
  CONTROL_SHADOW_OFFSET_X,
  CONTROL_SHADOW_OFFSET_Y,
  CONTROL_SHADOW_BLUR,
];

/**
 * Reads props for one state through the element's theme style chain (`dropdown` → `field`). A
 * state is composed over the base, so it falls back to the base wherever it leaves a prop unset.
 * @param {ImmutableMap} element
 * @param {ImmutableMap} theme
 * @param {string} layout
 * @param {string[]} propNames - Base prop codes, e.g. `label.color`.
 * @param {string} [state] - A `FORM_STATE_*`; the Default state reads the base only.
 * @returns {ImmutableMap} The values, nested by prop path.
 */
const readFormFieldProps = (element, theme, layout, propNames, state = FORM_STATE_DEFAULT) =>
  state === FORM_STATE_DEFAULT
    ? gatherProps(element, layout, propNames, false, theme)
    : gatherProps(element, layout, propNames, false, theme, state, true);

/**
 * @param {ImmutableMap} props - From `readFormFieldProps`.
 * @param {string} propName
 * @returns {unknown} The value as plain JS.
 */
const valueAt = (props, propName) => {
  const value = props.getIn(propName.split("."));
  return Immutable.Iterable.isIterable(value) ? value.toJS() : value;
};

/**
 * Resolves one state-addressed prop the way the stage does, so a panel control shows and pins the
 * value the preview draws. Takes `inheritProp`'s arguments, so the property mapper can use it in
 * its place.
 * @param {ImmutableMap} element - The form-field element.
 * @param {string} layout - `desktop` or `mobile`.
 * @param {string} propName - State-addressed prop code, e.g. `focus.control.text.color`.
 * @param {ImmutableMap} theme - The design theme.
 * @returns {unknown}
 */
export const resolveFormFieldValue = (element, layout, propName, theme) => {
  const [prefix, ...rest] = propName.split(".");
  const isState = prefix === FORM_STATE_FOCUS || prefix === FORM_STATE_ERROR;

  const basePropName = isState ? rest.join(".") : propName;
  const state = isState ? prefix : FORM_STATE_DEFAULT;

  return valueAt(readFormFieldProps(element, theme, layout, [basePropName], state), basePropName);
};

/**
 * @param {Object<string, string>} style - Collects the vars.
 * @param {ImmutableMap} stateProps - The state's values, from `readFormFieldProps`.
 * @param {ImmutableMap} theme
 */
const addShadowVars = (style, stateProps, theme) => {
  const read = (propName) => valueAt(stateProps, propName);
  const shadowStyle = read(CONTROL_SHADOW_STYLE);
  const shadowColor = SHADOW_VARS[shadowStyle] && read(CONTROL_SHADOW_COLOR);

  // Both layers are always written: an unset layer's vars would invalidate the whole box-shadow.
  Object.entries(SHADOW_VARS).forEach(([kind, [x, y, blur, colorVar]]) => {
    const isActive = kind === shadowStyle;

    style[x] = isActive ? px(read(CONTROL_SHADOW_OFFSET_X) ?? 0) : "0px";
    style[y] = isActive ? px(read(CONTROL_SHADOW_OFFSET_Y) ?? 0) : "0px";
    style[blur] = isActive ? px(read(CONTROL_SHADOW_BLUR) ?? 0) : "0px";
    style[colorVar] = isActive && shadowColor ? color(shadowColor, theme) : "transparent";
  });
};

/**
 * Resolves a form field's theme-inherited look for one state into what its widget reads: `--*`
 * custom properties, plus the discriminator attributes. The widget has no per-state properties, so
 * the stage draws a state by writing that state's values.
 * @param {Object} params
 * @param {ImmutableMap} params.element - The form-field element.
 * @param {ImmutableMap} params.theme - The design theme.
 * @param {string} params.layout - `desktop` or `mobile`.
 * @param {string} [params.state] - A `FORM_STATE_*`; the Default state when omitted.
 * @returns {{ style: Object<string, string>, attributes: Object<string, string | undefined> }}
 */
export const resolveFormFieldStyles = ({ element, theme, layout, state = FORM_STATE_DEFAULT }) => {
  const stateProps = readFormFieldProps(
    element,
    theme,
    layout,
    [...STATE_VARS.map(([, propName]) => propName), ...SHADOW_PROPS],
    state,
  );
  const defaultProps = readFormFieldProps(element, theme, layout, [
    ...DEFAULT_ONLY_VARS.map(([, propName]) => propName),
    ...WIDGET_ATTRIBUTES.map(([, propName]) => propName),
  ]);
  const style = {};

  const addVar = (props, [varName, propName, format]) => {
    const value = valueAt(props, propName);
    if (value !== undefined && value !== null && value !== "") {
      style[varName] = format(value, theme);
    }
  };

  STATE_VARS.forEach((entry) => addVar(stateProps, entry));
  addShadowVars(style, stateProps, theme);
  DEFAULT_ONLY_VARS.forEach((entry) => addVar(defaultProps, entry));

  // Unresolved attributes stay as `undefined` so assigning them clears a value a previous style set.
  const attributes = Object.fromEntries(
    WIDGET_ATTRIBUTES.map(([name, propName]) => [name, valueAt(defaultProps, propName)]),
  );

  return { style, attributes };
};
